import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Filter, ArrowLeft, Video, Clock, Calendar } from 'lucide-react';
import { getSubjectGradient } from '../utils/constants';
import { VideoPlayerModal } from '../components/VideoPlayerModal';
import { ScheduleModal } from '../components/ScheduleModal';
import { useProgramContext } from '../contexts/ProgramContext';
interface VideoSolution {
  id: number;
  title: string;
  topic: string;
  duration: string;
  thumbnail: string;
  description?: string;
}
export function VideoSolutionsLibrary() {
  const {
    subject = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    gorevEkle
  } = useProgramContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<VideoSolution | null>(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleItemData, setScheduleItemData] = useState({
    title: '',
    type: 'video' as 'resource' | 'test' | 'video' | 'homework',
    subject: '',
    videoId: 0
  });
  // Get gradient colors based on subject
  const gradient = getSubjectGradient(subject);
  // Format the subject name for display
  const formatSubjectName = (subject: string) => {
    return subject.charAt(0).toUpperCase() + subject.slice(1);
  };
  // Generate video solutions based on subject
  const generateVideoSolutions = () => {
    const subjectName = formatSubjectName(subject);
    // Generate topics based on subject
    let topics: string[] = [];
    if (subject === 'turkce') {
      topics = ['Sözcükte Anlam', 'Deyimler ve Atasözleri', 'Cümlede Anlam', 'Paragraf', 'Söz Sanatları'];
    } else if (subject === 'matematik') {
      topics = ['Üslü Sayılar', 'Köklü Sayılar', 'Olasılık', 'Eşitsizlikler', 'Üçgenler'];
    } else if (subject === 'fen') {
      topics = ['DNA ve Genetik Kod', 'Basınç', 'Madde ve Endüstri', 'Basit Makineler', 'Elektrik Yükleri'];
    } else {
      topics = ['Konu 1', 'Konu 2', 'Konu 3', 'Konu 4', 'Konu 5'];
    }
    // Generate video solutions for each topic
    const videoSolutions: VideoSolution[] = [];
    topics.forEach((topic, topicIndex) => {
      // Generate 3-5 videos per topic
      const videoCount = Math.floor(Math.random() * 3) + 3;
      for (let i = 1; i <= videoCount; i++) {
        videoSolutions.push({
          id: topicIndex * 10 + i,
          title: `${topic} - ${i * 5} Soru Çözümü`,
          topic: topic,
          duration: `${Math.floor(Math.random() * 10) + 5}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')}`,
          thumbnail: `https://images.unsplash.com/photo-${1550000000000 + topicIndex * 1000 + i * 100}?w=300&h=180&fit=crop&q=80`,
          description: `Bu videoda ${topic} konusuna ait ${i * 5} adet soru çözümü bulunmaktadır. Çözümlü sorular ile konuyu pekiştirebilirsiniz.`
        });
      }
    });
    return {
      topics,
      videoSolutions
    };
  };
  const {
    topics,
    videoSolutions
  } = generateVideoSolutions();
  // Filter videos based on search term and selected topic
  const filteredVideos = videoSolutions.filter(video => {
    const matchesSearch = video.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTopic = selectedTopic === null || video.topic === selectedTopic;
    return matchesSearch && matchesTopic;
  });
  // Handle adding video to schedule
  const handleAddToSchedule = (data: {
    title: string;
    subject: string;
    type: string;
    day: string;
    startTime: string;
    endTime: string;
    notes?: string;
  }) => {
    // Convert to the format expected by ProgramContext
    gorevEkle({
      dersAdi: formatSubjectName(subject).toUpperCase(),
      videoAdi: data.title,
      konuAdi: selectedTopic || 'Genel',
      gun: data.day,
      saatAraligi: `${data.startTime}-${data.endTime}`,
      notlar: data.notes,
      tamamlandi: false,
      etkinlikTipi: 'test',
      videoDetay: 'Soru Çözüm Videosu'
    });
    // Show success message
    alert(`${data.title} programınıza eklendi!`);
    setScheduleModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-3378">
      <div className="flex items-center gap-4 mb-6" data-id="element-3379">
        <button onClick={() => navigate(-1)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border bg-gradient-to-r ${gradient.from} ${gradient.to} text-white hover:opacity-90 transition-all`} data-id="element-3380">
          <ArrowLeft className="w-5 h-5" data-id="element-3381" />
          Geri
        </button>
        <h1 className="text-2xl font-bold" data-id="element-3382">
          {formatSubjectName(subject)} - Soru Çözüm Videoları
        </h1>
      </div>

      {/* Search and Filter */}
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-3383">
        <div className="relative bg-black/90 rounded-xl p-6" data-id="element-3384">
          <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-3385">
            <div className="relative flex-1" data-id="element-3386">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-3387" />
              <input type="text" placeholder="Video ara..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full bg-white/5 text-white rounded-lg pl-10 pr-4 py-2 focus:ring-2 ring-purple-500/20" data-id="element-3388" />
            </div>
            <div className="relative" data-id="element-3389">
              <select value={selectedTopic || ''} onChange={e => setSelectedTopic(e.target.value === '' ? null : e.target.value)} className="appearance-none bg-white/5 text-white rounded-lg px-10 py-2 focus:ring-2 ring-purple-500/20" style={{
              backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 1rem center',
              backgroundSize: '1em'
            }} data-id="element-3390">
                <option value="" data-id="element-3391">Tüm Konular</option>
                {topics.map(topic => <option key={topic} value={topic} data-id="element-3392">
                    {topic}
                  </option>)}
              </select>
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-3393" />
            </div>
          </div>
        </div>
      </div>

      {/* Video Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3394">
        {filteredVideos.map(video => <div key={video.id} className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 group" data-id="element-3395">
            <div className="relative bg-black/90 rounded-lg" data-id="element-3396">
              <div className="relative aspect-video cursor-pointer" onClick={() => setSelectedVideo(video)} data-id="element-3397">
                <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" data-id="element-3398" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-70" data-id="element-3399"></div>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3400">
                  <div className="p-4 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors" data-id="element-3401">
                    <Play className="w-10 h-10" fill="white" data-id="element-3402" />
                  </div>
                </div>
                <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/60 rounded-md flex items-center" data-id="element-3403">
                  <Clock className="w-3 h-3 mr-1 text-purple-400" data-id="element-3404" />
                  <span className="text-xs" data-id="element-3405">{video.duration}</span>
                </div>
              </div>
              <div className="p-4" data-id="element-3406">
                <div className="flex justify-between items-start mb-2" data-id="element-3407">
                  <h3 className="font-medium line-clamp-2" data-id="element-3408">{video.title}</h3>
                  <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors flex-shrink-0 ml-2" onClick={() => {
                setScheduleModalOpen(true);
                setScheduleItemData({
                  title: `Soru Çözüm Videosu İzleme: ${video.title}`,
                  type: 'video',
                  subject: formatSubjectName(subject),
                  videoId: video.id
                });
              }} title="Programa Ekle" data-id="element-3409">
                    <Calendar className="w-5 h-5 text-purple-400 hover:text-white" data-id="element-3410" />
                  </button>
                </div>
                <p className="text-sm text-gray-400" data-id="element-3411">{video.topic}</p>
              </div>
            </div>
          </div>)}
      </div>

      {filteredVideos.length === 0 && <div className="text-center py-12 text-gray-400" data-id="element-3412">
          Arama kriterlerinize uygun video bulunamadı.
        </div>}

      {/* Video Player Modal */}
      {selectedVideo && <VideoPlayerModal video={selectedVideo} onClose={() => setSelectedVideo(null)} data-id="element-3413" />}

      {/* Schedule Modal */}
      {scheduleModalOpen && <ScheduleModal initialData={scheduleItemData} onClose={() => setScheduleModalOpen(false)} onSave={handleAddToSchedule} data-id="element-3414" />}
    </div>;
}